import { useCurrentUser } from "@/lib/auth";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 min d'inactivité → déconnexion
const IDLE_WARN_MS = 60 * 1000; // avertissement 1 min avant

/**
 * Anti « poste laissé ouvert » : monté dans les shells authentifiés, il déclenche
 * une déconnexion après 15 min d'inactivité, précédée d'un avertissement à 1 min.
 * Ne s'arme QUE si l'utilisateur est authentifié (guest/landing non concernés).
 *
 * Rend une modale d'avertissement (overlay léger, sans portail Radix) uniquement
 * pendant la fenêtre finale ; sinon ne rend rien.
 */
export default function IdleLogoutGuard() {
  const { isAuthenticated, logout } = useCurrentUser();
  const { warning, remainingMs, stayConnected } = useIdleLogout({
    timeoutMs: IDLE_TIMEOUT_MS,
    warnMs: IDLE_WARN_MS,
    enabled: isAuthenticated,
    onIdle: logout,
  });

  if (!warning) return null;

  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-logout-title"
      aria-describedby="idle-logout-desc"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      // Reste cliquable même si un Dialog Radix ouvert a posé pointer-events:none
      // en inline sur <body> (l'avertissement peut surgir par-dessus une modale).
      style={{ pointerEvents: "auto" }}
      data-testid="idle-warning"
    >
      <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
        <div className="flex items-center gap-2.5 rounded-t-lg bg-navy px-5 py-4 text-white">
          <ShieldAlert className="h-5 w-5 text-gold shrink-0" />
          <h2 id="idle-logout-title" className="text-base font-bold tracking-tight">
            Déconnexion imminente
          </h2>
        </div>
        <div className="space-y-4 px-5 py-4">
          <p id="idle-logout-desc" className="text-sm text-slate-600">
            Vous allez être déconnecté dans{" "}
            <span className="font-bold text-navy" data-testid="idle-seconds">
              {seconds}
            </span>{" "}
            seconde{seconds > 1 ? "s" : ""} pour inactivité. Cliquez pour rester
            connecté.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => logout()}
              data-testid="idle-logout-now"
            >
              Se déconnecter
            </Button>
            <Button
              onClick={stayConnected}
              className="bg-navy text-white hover:bg-navy/90"
              data-testid="idle-stay-connected"
            >
              Rester connecté
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
